// particles.ts - Trail-based thrust system for visual thrust feedback
// Uses circular buffer to avoid per-frame array allocations

import type { Vec2 } from "@game/core";

export interface ThrustTrailPoint {
  pos: Vec2;
  timestamp: number;
}

/**
 * Circular buffer for trail points - eliminates filter/slice allocations.
 * Points are stored in a pre-allocated array and overwritten when the buffer wraps.
 */
export interface ThrustTrailBuffer {
  points: ThrustTrailPoint[]; // Pre-allocated array of maxLength
  head: number; // Next write position (0 to maxLength-1)
  count: number; // Current number of valid points (0 to maxLength)
  readonly maxLength: number; // Buffer capacity (TRAIL_LENGTH)
}

export interface ThrustTrail {
  playerId: string;
  buffer: ThrustTrailBuffer;
  color: string;
  lastUpdate: number;
  isActive: boolean;
  engineOffset: { x: number; y: number };
}

export const THRUST_CONSTANTS = {
  TRAIL_LENGTH: 12, // Slightly shorter trails for performance
  TRAIL_LIFETIME: 220, // Shorter lifetime → fewer segments
  ENGINE_OFFSET: 8,
  NORMAL_COLOR: "#00BFFF",
  NORMAL_COLOR_RGB: "0, 191, 255",
  STRAFE_COLOR: "#FF4500",
  STRAFE_COLOR_RGB: "255, 69, 0",
  GRADIENT_TEMPLATE_LENGTH: 100,
} as const;

/**
 * Module-level cached gradients to avoid per-frame allocation.
 * These are template gradients from (0,0) to (100,0) that get
 * transformed via canvas context to match each trail's orientation.
 */
let cachedNormalGradient: CanvasGradient | null = null;
let cachedStrafeGradient: CanvasGradient | null = null;
let gradientInitContext: CanvasRenderingContext2D | null = null;

function initThrustGradients(ctx: CanvasRenderingContext2D): void {
  if (cachedNormalGradient && gradientInitContext === ctx) return;

  const len = THRUST_CONSTANTS.GRADIENT_TEMPLATE_LENGTH;

  cachedNormalGradient = ctx.createLinearGradient(0, 0, len, 0);
  cachedNormalGradient.addColorStop(0, "rgba(0, 191, 255, 0)");
  cachedNormalGradient.addColorStop(0.3, "rgba(0, 191, 255, 0.2)");
  cachedNormalGradient.addColorStop(0.7, "rgba(0, 191, 255, 0.6)");
  cachedNormalGradient.addColorStop(1, "rgba(0, 191, 255, 0.9)");

  cachedStrafeGradient = ctx.createLinearGradient(0, 0, len, 0);
  cachedStrafeGradient.addColorStop(0, "rgba(255, 69, 0, 0)");
  cachedStrafeGradient.addColorStop(0.3, "rgba(255, 69, 0, 0.2)");
  cachedStrafeGradient.addColorStop(0.7, "rgba(255, 69, 0, 0.6)");
  cachedStrafeGradient.addColorStop(1, "rgba(255, 69, 0, 0.9)");

  gradientInitContext = ctx;
}

/**
 * Add a point to the circular buffer without any array allocations.
 * Points are written at the head position, overwriting old data when full.
 */
export function bufferPush(
  buffer: ThrustTrailBuffer,
  point: ThrustTrailPoint,
): void {
  buffer.points[buffer.head] = point;
  buffer.head = (buffer.head + 1) % buffer.maxLength;
  if (buffer.count < buffer.maxLength) {
    buffer.count++;
  }
}

/**
 * Get the index of the oldest point in the circular buffer.
 * When buffer is full, oldest is at head (about to be overwritten).
 * When not full, oldest is at index 0.
 */
export function bufferOldestIndex(buffer: ThrustTrailBuffer): number {
  if (buffer.count < buffer.maxLength) {
    return 0;
  }
  return buffer.head; // When full, head points to oldest
}

/**
 * Get a point from the buffer by logical index (0 = oldest, count-1 = newest).
 * Returns undefined if index is out of bounds.
 */
export function bufferGetByLogicalIndex(
  buffer: ThrustTrailBuffer,
  logicalIndex: number,
): ThrustTrailPoint | undefined {
  if (logicalIndex < 0 || logicalIndex >= buffer.count) {
    return undefined;
  }
  const startIndex = bufferOldestIndex(buffer);
  const actualIndex = (startIndex + logicalIndex) % buffer.maxLength;
  return buffer.points[actualIndex];
}

/**
 * Compact the buffer by removing expired points from the beginning.
 * Only adjusts count - no array allocations or copying.
 * Expired points remain in memory but are logically removed.
 */
function bufferExpireOld(buffer: ThrustTrailBuffer, now: number): void {
  const startIndex = bufferOldestIndex(buffer);
  let expiredCount = 0;

  // Count consecutive expired points from the oldest
  for (let i = 0; i < buffer.count; i++) {
    const actualIndex = (startIndex + i) % buffer.maxLength;
    const point = buffer.points[actualIndex];
    if (point && now - point.timestamp >= THRUST_CONSTANTS.TRAIL_LIFETIME) {
      expiredCount++;
    } else {
      break; // Stop at first valid point
    }
  }

  // Logically remove expired points by reducing count
  // Head stays the same - we're removing from the "tail" (oldest end)
  buffer.count -= expiredCount;
}

export function updateThrustTrail(
  trail: ThrustTrail,
  playerX: number,
  playerY: number,
  thrustAngle: number,
  isThrusting: boolean,
  playerScale: number = 1.0,
): void {
  const currentTime = Date.now();

  if (isThrusting) {
    const scaledEngineOffset = THRUST_CONSTANTS.ENGINE_OFFSET * playerScale;
    const engineX =
      playerX + Math.cos(thrustAngle + Math.PI) * scaledEngineOffset;
    const engineY =
      playerY + Math.sin(thrustAngle + Math.PI) * scaledEngineOffset;

    // Zero-allocation push to circular buffer
    bufferPush(trail.buffer, {
      pos: { x: engineX, y: engineY },
      timestamp: currentTime,
    });

    trail.color = THRUST_CONSTANTS.NORMAL_COLOR;
    trail.isActive = true;
    trail.lastUpdate = currentTime;
  } else {
    trail.isActive = false;
  }

  // Zero-allocation expiry - just adjusts count, no filter/slice
  bufferExpireOld(trail.buffer, currentTime);
}

/**
 * Create a pre-allocated circular buffer for trail points.
 * Buffer is filled with placeholder points to avoid sparse array issues.
 */
function createTrailBuffer(): ThrustTrailBuffer {
  const maxLength = THRUST_CONSTANTS.TRAIL_LENGTH;
  const points: ThrustTrailPoint[] = new Array(maxLength);
  // Pre-fill with placeholder points to ensure dense array
  for (let i = 0; i < maxLength; i++) {
    points[i] = { pos: { x: 0, y: 0 }, timestamp: 0 };
  }
  return {
    points,
    head: 0,
    count: 0,
    maxLength,
  };
}

export function createThrustTrail(playerId: string): ThrustTrail {
  return {
    playerId,
    buffer: createTrailBuffer(),
    color: THRUST_CONSTANTS.NORMAL_COLOR,
    lastUpdate: Date.now(),
    isActive: false,
    engineOffset: { x: 0, y: 0 },
  };
}

export function renderThrustTrail(
  ctx: CanvasRenderingContext2D,
  trail: ThrustTrail,
  isVisible: (x: number, y: number, margin?: number) => boolean,
  getWrappedPosition: (x: number, y: number) => { x: number; y: number },
  playerScale: number = 1.0,
): void {
  const buffer = trail.buffer;
  if (buffer.count < 2) return;

  // Check visibility using circular buffer iteration
  let hasVisiblePoints = false;
  const startIndex = bufferOldestIndex(buffer);
  for (let i = 0; i < buffer.count; i++) {
    const actualIndex = (startIndex + i) % buffer.maxLength;
    const point = buffer.points[actualIndex];
    if (point && isVisible(point.pos.x, point.pos.y)) {
      hasVisiblePoints = true;
      break;
    }
  }
  if (!hasVisiblePoints) return;

  initThrustGradients(ctx);

  // Get oldest and newest points from circular buffer
  const oldestPoint = bufferGetByLogicalIndex(buffer, 0);
  const newestPoint = bufferGetByLogicalIndex(buffer, buffer.count - 1);
  if (!oldestPoint || !newestPoint) return;

  const wrappedOldest = getWrappedPosition(
    oldestPoint.pos.x,
    oldestPoint.pos.y,
  );
  const wrappedNewest = getWrappedPosition(
    newestPoint.pos.x,
    newestPoint.pos.y,
  );

  const deltaX = wrappedNewest.x - wrappedOldest.x;
  const deltaY = wrappedNewest.y - wrappedOldest.y;
  const trailLength = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

  if (trailLength < 1) return;

  const trailAngle = Math.atan2(deltaY, deltaX);
  const templateLength = THRUST_CONSTANTS.GRADIENT_TEMPLATE_LENGTH;
  const scaleFactor = trailLength / templateLength;
  const isStrafe = trail.color === THRUST_CONSTANTS.STRAFE_COLOR;
  const gradient = isStrafe ? cachedStrafeGradient : cachedNormalGradient;

  if (!gradient) return;

  ctx.save();

  ctx.translate(wrappedOldest.x, wrappedOldest.y);
  ctx.rotate(trailAngle);
  ctx.scale(scaleFactor, 1);

  ctx.strokeStyle = gradient;
  ctx.lineWidth = 5 * playerScale;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowColor = trail.color;
  ctx.shadowBlur = 6 * playerScale;

  ctx.beginPath();
  ctx.moveTo(0, 0);

  const cosAngle = Math.cos(-trailAngle);
  const sinAngle = Math.sin(-trailAngle);
  const invScale = 1 / scaleFactor;

  let lastLocalX = 0;
  let lastLocalY = 0;

  // Iterate circular buffer from index 1 (skip oldest, already at origin)
  for (let i = 1; i < buffer.count; i++) {
    const point = bufferGetByLogicalIndex(buffer, i);
    if (!point) continue;

    const wrappedPoint = getWrappedPosition(point.pos.x, point.pos.y);

    const relativeX = wrappedPoint.x - wrappedOldest.x;
    const relativeY = wrappedPoint.y - wrappedOldest.y;

    const rotatedX = relativeX * cosAngle - relativeY * sinAngle;
    const rotatedY = relativeX * sinAngle + relativeY * cosAngle;

    const localX = rotatedX * invScale;
    const localY = rotatedY;

    const localDeltaX = localX - lastLocalX;
    const localDeltaY = localY - lastLocalY;
    if (localDeltaX * localDeltaX + localDeltaY * localDeltaY > 0.5) {
      ctx.lineTo(localX, localY);
      lastLocalX = localX;
      lastLocalY = localY;
    }
  }

  ctx.stroke();
  ctx.restore();
}
